<script setup lang="ts">
import type { Legend } from "@/types/legend.types";
import { useUpdateLegendUsers } from "@/stores/queries";
import { computed, ref, watch } from "vue";
import { useGetUsers } from "@/stores/queries";
import type { UserProperties } from "@/APIClient";
import DRBtn from "../UI/DRBtn.vue";
import DRBtnIcon from "../UI/DRBtnIcon.vue";
import DRUserAvatar from "../UI/DRUserAvatar.vue";
import { useScreen } from "@/composable/useResponsive";

const props = defineProps<{
  legend: Legend;
}>();

const id = computed(() => props.legend.id);

const { data } = useGetUsers();

const users = computed(() => {
  if (!data.value) return [];
  const res: UserProperties[] = [...data.value];
  res.sort((a, b) => {
    if (a.organization?.name === b.organization?.name) {
      return a.full_name.localeCompare(b.full_name);
    }
    return a.organization?.name.localeCompare(b.organization?.name ?? "") ?? 0;
  });
  return res;
});

const { mutateAsync: onUpdateUsers, isPending: isUpdating } =
  useUpdateLegendUsers(id);

const concernedUsers = ref<number[]>(
  props.legend.users?.map((user) => user.id) ?? []
);

watch(
  () => props.legend.users,
  (newVal) => {
    concernedUsers.value = newVal?.map((user) => user.id) ?? [];
  },
  { immediate: true }
);

const displayedUsers = computed(() => {
  const ids = props.legend.users?.map((user) => user.id) ?? [];
  return users.value.filter((user) => ids.includes(user.id));
});

const isDifferent = computed(() => {
  return (
    concernedUsers.value.length !== props.legend.users?.length ||
    concernedUsers.value.some(
      (userId) => !props.legend.users?.some((user) => user.id === userId)
    )
  );
});

const isError = computed(() => {
  return !isDifferent.value || concernedUsers.value.length === 0;
});

const canSaveUsers = computed(() => {
  return !isError.value && !isUpdating.value;
});
const showModal = ref(false);

const openModal = () => {
  showModal.value = true;
  concernedUsers.value = props.legend.users?.map((user) => user.id) ?? [];
};

const cancel = () => {
  showModal.value = false;
};

const confirm = async () => {
  if (!canSaveUsers.value) return;
  onUpdateUsers(concernedUsers.value, {
    onSuccess: () => {
      showModal.value = false;
    },
  });
};

const { screen } = useScreen();
</script>

<template>
  <div class="d-flex gap-2 align-items-center">
    <el-tooltip
      class="box-item"
      effect="light"
      :content="displayedUsers?.join(', ')"
      placement="left-start"
    >
      <template #content>
        <div class="d-flex flex-column gap-2">
          <div
            v-for="user in displayedUsers"
            :key="user.id"
            class="d-flex align-items-center gap-2"
          >
            <DRUserAvatar :user="user" :size="20" />
            <div>{{ user.full_name }}</div>
          </div>
        </div>
      </template>

      <div style="width: 100px">
        {{ displayedUsers?.length }} {{ $t("users") }}
      </div>
    </el-tooltip>
    <DRBtnIcon @click="openModal" :size="30" variant="outline" icon="bi-people">
    </DRBtnIcon>
  </div>

  <el-dialog
    v-model="showModal"
    :title="$t('access.rights')"
    :width="screen.gt.sm ? '500px' : '100%'"
    append-to-body
    :close-on-click-modal="false"
    :close-on-press-escape="false"
  >
    <div class="mb-2">
      <span class="text-dark">
        {{ $t("dashboard:legends.accessRights") }}
      </span>
    </div>
    <el-form label-position="top">
      <el-form-item>
        <el-select
          v-model="concernedUsers"
          multiple
          collapse-tags
          collapse-tags-tooltip
          :placeholder="$t('users')"
          :max-collapse-tags="3"
        >
          <el-option
            v-for="user in users"
            :key="user.id"
            :label="user.full_name"
            :value="user.id"
          />
        </el-select>
        <div class="w-100">
          <div v-if="!concernedUsers?.length">
            <span class="text-danger">{{
              $t("dashboard:legends.error.emptyList")
            }}</span>
          </div>
        </div>
      </el-form-item>
    </el-form>
    <template #footer>
      <div class="dialog-footer d-flex justify-content-end gap-2">
        <DRBtn :label="$t('cancel')" variant="outline" @click="cancel"></DRBtn>

        <DRBtn
          :label="$t('dashboard:save')"
          :disabled="isError || isUpdating"
          :loading="isUpdating"
          variant="primary"
          @click="confirm"
        >
        </DRBtn>
      </div>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped></style>
